import { type V2, closestOnSegment, dist, dist2, lerp, smoothstep } from '../core/math';
import type { Rng } from '../core/rng';
import { AGENT_RADIUS, alongPolyline, band, fbm2, polylineDistance, scatter } from './build';
import type { Collider, PortalDef, PropInstance, ZoneLayout } from './types';
import { ZoneBuilder, type ZoneBuilderOptions } from './ZoneBuilder';

/**
 * Enclosed spaces (dungeons, raid gates, arenas, caverns).
 *
 * The author carves floor space out of solid rock with rooms (circles),
 * halls (rectangles) and corridors (polylines with a width); holes cut pits or
 * rock masses back out. Together they form a signed distance field (negative
 * on the floor). On `build()`:
 *
 * - the walkable area becomes the floor eroded by `inset`, traced with
 *   marching squares into one even-odd `walkable` polygon (loops joined by
 *   zero-width bridges), or a plain circle for a single round arena;
 * - terrain rises into dark rock behind far-side walls and stays low (or drops
 *   into a chasm) on camera-facing (+z, south) edges so the quarter-view
 *   camera is never blocked;
 * - boundaries are lined with the zone's wall kit: wall pieces and posts on far
 *   sides, lights along the critical path (off-path spaces stay dark), low
 *   rubble towards the camera, rock chunks in the cliff and clutter at the
 *   foot of the walls.
 *
 * Conventions: the camera looks north (-z), so critical paths run south → north
 * (start at the bottom of the screen, boss at the top). Clock positions:
 * 12 o'clock = north (-z), 3 o'clock = east (+x).
 */

/** Ground-layer roles (indices into `ground`). */
export interface DungeonPaint {
  floor: number;
  /** Runner painted along the critical path. */
  path: number;
  /** Rock walls and cliffs. */
  wall: number;
  /** Noise patches and wall-foot grime. */
  accent: number;
}

export interface KitPiece {
  model: string;
  scale?: number | [number, number];
  tint?: number;
  weight?: number;
  /** Footprint radius at scale 1 (keeps rocks from spilling onto the floor). */
  r?: number;
}

export interface DungeonKit {
  /**
   * Tall pieces on far-side edges, laid end to end. `length` is the model's
   * length along its local x axis at scale 1 (`dungeon_wall`: 8 m) and
   * `depth` its thickness; pieces are spaced `length × scale` apart.
   */
  wall?: Omit<KitPiece, 'scale'> & { length: number; depth?: number; scale?: number };
  /** A post (pillar) replaces every `every`-th wall slot. */
  post?: KitPiece & { every: number };
  /** A light (brazier) replaces every `every`-th slot on critical-path edges. */
  light?: KitPiece & { every: number };
  /** Low pieces on camera-facing edges. */
  low?: KitPiece[];
  /** Rock chunks half-buried in the rising cliff. */
  rocks?: KitPiece[];
  /** Pieces for camera-facing edges (default: `rocks` at 45% scale). Keep them low. */
  nearRocks?: KitPiece[];
  rockSpacing?: number;
  /** Small, collider-free clutter at the foot of the walls. */
  clutter?: KitPiece[];
  clutterChance?: number;
}

export interface DungeonBuilderOptions extends ZoneBuilderOptions {
  /** Cliff height above the floor behind far-side edges (m). */
  wallHeight?: number;
  /** Height behind camera-facing edges; negative drops into a chasm. */
  nearWallHeight?: number;
  /** Extra rise per metre beyond the cliff lip on far sides. */
  wallSlope?: number;
  /** Rock roughness amplitude on cliffs. */
  wallRough?: number;
  /** Walkable inset from the floor edge (keeps actors off the wall props). */
  inset?: number;
  /** Depth of chasms beyond `drop` edges (m). */
  dropDepth?: number;
  paint?: DungeonPaint;
  accentAmount?: number;
  floorNoise?: number;
  kit?: DungeonKit;
}

export interface CarveOptions {
  /** Floor height (m). Corridors without `y`/`ys` ramp between the floors they join. */
  y?: number;
  /** Ground layer painted on this floor (default `paint.floor`). */
  layer?: number;
  /** Part of the critical path: lit by warm lights. Off-path spaces stay dark. */
  path?: boolean;
  /** Line the boundary with the wall kit (default true). Rocks are always added. */
  walls?: boolean;
  /**
   * What lies beyond this space's edge: `wall` (default) rises into a cliff
   * on far sides and stays low toward the camera; `low` stays low all round;
   * `drop` falls into a chasm all round (bridges, floating platforms).
   */
  edge?: 'wall' | 'low' | 'drop';
}

type Shape =
  | { kind: 'circle'; x: number; z: number; r: number }
  | { kind: 'rect'; x: number; z: number; hw: number; hd: number; c: number; s: number; round: number }
  | { kind: 'path'; pts: V2[]; hw: number; ys: number[] | null };

export type HoleShape = { kind: 'circle'; x: number; z: number; r: number } | { kind: 'rect'; x: number; z: number; hw: number; hd: number; rot?: number; round?: number };

interface Bound {
  x: number;
  z: number;
  r: number;
}
interface Carve {
  shape: Shape;
  bound: Bound;
  y: number;
  layer: number;
  path: boolean;
  walls: boolean;
  edge: 'wall' | 'low' | 'drop';
}
interface Hole {
  shape: Shape;
  bound: Bound;
  mode: 'pit' | 'rock';
  depth: number;
}

/** Floor lip before the cliff starts rising (wall props stand on it). */
const FLAT = 0.9;
/** Horizontal run of the cliff face. */
const RAMP = 2.6;
/** Softness of floor-height blending between overlapping spaces. */
const BLEND = 1.4;
const CONTOUR_STEP = 0.5;
/** Opening width of the `arch` model at scale 1. */
const ARCH_WIDTH = 4.9;

// Scratch output of evalShape (single-threaded; avoids allocations in hot loops).
const S = { d: 0, nx: 0, nz: 0, y: 0 };

function evalShape(sh: Shape, y0: number, x: number, z: number): void {
  switch (sh.kind) {
    case 'circle': {
      const dx = x - sh.x;
      const dz = z - sh.z;
      const l = Math.hypot(dx, dz);
      S.d = l - sh.r;
      if (l > 1e-9) {
        S.nx = dx / l;
        S.nz = dz / l;
      } else {
        S.nx = 0;
        S.nz = -1;
      }
      S.y = y0;
      return;
    }
    case 'rect': {
      const dx = x - sh.x;
      const dz = z - sh.z;
      const lx = dx * sh.c - dz * sh.s;
      const lz = dx * sh.s + dz * sh.c;
      const qx = Math.abs(lx) - (sh.hw - sh.round);
      const qz = Math.abs(lz) - (sh.hd - sh.round);
      const ox = qx > 0 ? qx : 0;
      const oz = qz > 0 ? qz : 0;
      const out = Math.hypot(ox, oz);
      S.d = out + Math.min(Math.max(qx, qz), 0) - sh.round;
      let ax: number;
      let az: number;
      if (out > 1e-9) {
        ax = (lx < 0 ? -ox : ox) / out;
        az = (lz < 0 ? -oz : oz) / out;
      } else if (qx > qz) {
        ax = lx < 0 ? -1 : 1;
        az = 0;
      } else {
        ax = 0;
        az = lz < 0 ? -1 : 1;
      }
      S.nx = ax * sh.c + az * sh.s;
      S.nz = -ax * sh.s + az * sh.c;
      S.y = y0;
      return;
    }
    case 'path': {
      const p = sh.pts;
      let best = Infinity;
      let bx = 0;
      let bz = 0;
      let bi = 0;
      let bt = 0;
      for (let i = 0; i < p.length - 1; i++) {
        const a = p[i] as V2;
        const b = p[i + 1] as V2;
        const abx = b.x - a.x;
        const abz = b.z - a.z;
        const l2 = abx * abx + abz * abz;
        let t = l2 > 1e-12 ? ((x - a.x) * abx + (z - a.z) * abz) / l2 : 0;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const cx = a.x + abx * t;
        const cz = a.z + abz * t;
        const dd = (x - cx) * (x - cx) + (z - cz) * (z - cz);
        if (dd < best) {
          best = dd;
          bx = cx;
          bz = cz;
          bi = i;
          bt = t;
        }
      }
      const l = Math.sqrt(best);
      S.d = l - sh.hw;
      if (l > 1e-9) {
        S.nx = (x - bx) / l;
        S.nz = (z - bz) / l;
      } else {
        S.nx = 0;
        S.nz = -1;
      }
      S.y = sh.ys ? lerp(sh.ys[bi] as number, sh.ys[bi + 1] as number, bt) : y0;
      return;
    }
  }
}

function boundOf(sh: Shape): Bound {
  if (sh.kind === 'circle') return { x: sh.x, z: sh.z, r: sh.r };
  if (sh.kind === 'rect') return { x: sh.x, z: sh.z, r: Math.hypot(sh.hw, sh.hd) };
  let x0 = Infinity;
  let z0 = Infinity;
  let x1 = -Infinity;
  let z1 = -Infinity;
  for (const p of sh.pts) {
    x0 = Math.min(x0, p.x);
    z0 = Math.min(z0, p.z);
    x1 = Math.max(x1, p.x);
    z1 = Math.max(z1, p.z);
  }
  const c = { x: (x0 + x1) / 2, z: (z0 + z1) / 2 };
  let r = 0;
  for (const p of sh.pts) r = Math.max(r, dist(c, p));
  return { ...c, r: r + sh.hw };
}

function rectShape(x: number, z: number, hw: number, hd: number, rot = 0, round = 0): Shape {
  return { kind: 'rect', x, z, hw, hd, c: Math.cos(rot), s: Math.sin(rot), round: Math.min(round, hw, hd) };
}

// ------------------------------------------------------------------ contours

/**
 * Marching squares over a (nx+1)×(nz+1) sample grid. Returns closed loops of
 * the iso-line `value = iso` ("inside" is `value < iso`). Border samples must
 * be outside so that every loop closes.
 */
export function marchingSquares(vals: Float64Array, nx: number, nz: number, x0: number, z0: number, step: number, iso: number): V2[][] {
  const W = nx + 1;
  const v = (i: number, j: number): number => (vals[j * W + i] as number) - iso;
  const E = 2 * W * (nz + 1);
  const adj = new Int32Array(E * 2).fill(-1);
  const link = (a: number, b: number): void => {
    if (adj[a * 2] === -1) adj[a * 2] = b;
    else adj[a * 2 + 1] = b;
    if (adj[b * 2] === -1) adj[b * 2] = a;
    else adj[b * 2 + 1] = a;
  };
  const H = (i: number, j: number): number => (j * W + i) * 2;
  const V = (i: number, j: number): number => (j * W + i) * 2 + 1;
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const a = v(i, j);
      const b = v(i + 1, j);
      const c = v(i + 1, j + 1);
      const d = v(i, j + 1);
      const idx = (a < 0 ? 1 : 0) | (b < 0 ? 2 : 0) | (c < 0 ? 4 : 0) | (d < 0 ? 8 : 0);
      if (idx === 0 || idx === 15) continue;
      const e0 = H(i, j);
      const e1 = V(i + 1, j);
      const e2 = H(i, j + 1);
      const e3 = V(i, j);
      const centreIn = (a + b + c + d) / 4 < 0;
      switch (idx) {
        case 1:
        case 14:
          link(e3, e0);
          break;
        case 2:
        case 13:
          link(e0, e1);
          break;
        case 3:
        case 12:
          link(e3, e1);
          break;
        case 4:
        case 11:
          link(e1, e2);
          break;
        case 6:
        case 9:
          link(e0, e2);
          break;
        case 7:
        case 8:
          link(e2, e3);
          break;
        case 5:
          if (centreIn) {
            link(e0, e1);
            link(e2, e3);
          } else {
            link(e3, e0);
            link(e1, e2);
          }
          break;
        case 10:
          if (centreIn) {
            link(e3, e0);
            link(e1, e2);
          } else {
            link(e0, e1);
            link(e2, e3);
          }
          break;
        default:
          break;
      }
    }
  }
  const point = (e: number): V2 => {
    const k = e >> 1;
    const i = k % W;
    const j = (k - i) / W;
    if ((e & 1) === 0) {
      const va = v(i, j);
      const t = va / (va - v(i + 1, j));
      return { x: x0 + (i + t) * step, z: z0 + j * step };
    }
    const va = v(i, j);
    const t = va / (va - v(i, j + 1));
    return { x: x0 + i * step, z: z0 + (j + t) * step };
  };
  const seen = new Uint8Array(E);
  const loops: V2[][] = [];
  for (let e = 0; e < E; e++) {
    if (adj[e * 2] === -1 || seen[e]) continue;
    const loop: V2[] = [];
    let prev = -1;
    let cur = e;
    while (cur >= 0 && !seen[cur]) {
      seen[cur] = 1;
      loop.push(point(cur));
      const n0 = adj[cur * 2] as number;
      const n1 = adj[cur * 2 + 1] as number;
      const next = n0 !== prev ? n0 : n1;
      prev = cur;
      cur = next;
    }
    if (loop.length >= 3) loops.push(loop);
  }
  return loops;
}

function simplifyOpen(pts: V2[], tol: number): V2[] {
  const n = pts.length;
  if (n <= 2) return pts.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack: number[] = [0, n - 1];
  while (stack.length > 0) {
    const e = stack.pop() as number;
    const s = stack.pop() as number;
    let md = -1;
    let mi = -1;
    const a = pts[s] as V2;
    const b = pts[e] as V2;
    for (let i = s + 1; i < e; i++) {
      const p = pts[i] as V2;
      const d = dist(p, closestOnSegment(p, a, b));
      if (d > md) {
        md = d;
        mi = i;
      }
    }
    if (mi > 0 && md > tol) {
      keep[mi] = 1;
      stack.push(s, mi, mi, e);
    }
  }
  return pts.filter((_, i) => keep[i] === 1);
}

/** Douglas–Peucker simplification of a closed ring (no repeated end point). */
export function simplifyRing(pts: V2[], tol: number): V2[] {
  const n = pts.length;
  if (n < 5) return pts.slice();
  const first = pts[0] as V2;
  let far = 1;
  let fd = -1;
  for (let i = 1; i < n; i++) {
    const d = dist2(pts[i] as V2, first);
    if (d > fd) {
      fd = d;
      far = i;
    }
  }
  const a = simplifyOpen(pts.slice(0, far + 1), tol);
  const b = simplifyOpen([...pts.slice(far), first], tol);
  return [...a.slice(0, -1), ...b.slice(0, -1)];
}

/**
 * Joins several loops into one even-odd polygon: each loop hangs off `origin`
 * by a zero-width bridge walked out and back, so bridges cancel in the
 * crossing test and the result is the XOR of the loops (outer rings plus holes).
 */
export function bridgeLoops(loops: V2[][], origin: V2): V2[] {
  const out: V2[] = [];
  for (const l of loops) {
    const first = l[0];
    if (!first) continue;
    out.push({ ...origin }, ...l, { ...first });
  }
  return out;
}

// ------------------------------------------------------------------ builder

export class DungeonBuilder extends ZoneBuilder {
  private readonly carves: Carve[] = [];
  private readonly holes: Hole[] = [];
  private readonly runners: { pts: V2[]; width: number }[] = [];
  private readonly paints: { fn: (x: number, z: number) => number; layer: number }[] = [];
  private readonly dp: Required<Omit<DungeonBuilderOptions, keyof ZoneBuilderOptions>>;
  private ds = new Float64Array(16);
  private dys = new Float64Array(16);
  private dnz = new Float64Array(16);
  /** Probe result: sdf, blended floor height, blended cliff top and far-side factor, owners. */
  private readonly pr = { d: 0, y: 0, top: 0, far: 0, carve: null as Carve | null, hole: null as Hole | null };
  private finished = false;
  /** Floor-edge loops (after `build()`), for zone-specific dressing. */
  edgeLoops: V2[][] = [];

  constructor(opts: DungeonBuilderOptions, rng: Rng) {
    super({ hills: 0, ...opts }, rng);
    this.dp = {
      wallHeight: opts.wallHeight ?? 7,
      nearWallHeight: opts.nearWallHeight ?? 1,
      wallSlope: opts.wallSlope ?? 0.45,
      wallRough: opts.wallRough ?? 0.8,
      inset: opts.inset ?? 0.5,
      dropDepth: opts.dropDepth ?? 9,
      paint: opts.paint ?? { floor: 0, path: 1, wall: 2, accent: 3 },
      accentAmount: opts.accentAmount ?? 0.6,
      floorNoise: opts.floorNoise ?? 0.08,
      kit: opts.kit ?? {},
    };
    this.addHeight(this.carveHeight);
    this.addSurface(this.carveSurface);
  }

  // -------------------------------------------------------------- carving

  private addCarve(shape: Shape, o: CarveOptions, y: number): this {
    if (this.finished) throw new Error('DungeonBuilder: carve after build()');
    this.carves.push({ shape, bound: boundOf(shape), y, layer: o.layer ?? this.dp.paint.floor, path: o.path ?? false, walls: o.walls ?? true, edge: o.edge ?? 'wall' });
    if (this.ds.length < this.carves.length) {
      const n = this.carves.length * 2;
      this.ds = new Float64Array(n);
      this.dys = new Float64Array(n);
      this.dnz = new Float64Array(n);
    }
    return this;
  }

  /** Round room. */
  room(x: number, z: number, r: number, o: CarveOptions = {}): this {
    return this.addCarve({ kind: 'circle', x, z, r }, o, o.y ?? 0);
  }

  /** Rectangular hall; `rot` follows prop rotation (local x = (cos, -sin)). */
  hall(x: number, z: number, hw: number, hd: number, o: CarveOptions & { rot?: number; round?: number } = {}): this {
    return this.addCarve(rectShape(x, z, hw, hd, o.rot ?? 0, o.round ?? 0.8), o, o.y ?? 0);
  }

  /**
   * Corridor along a polyline. Heights: `ys` per vertex, else constant `y`,
   * else a ramp between the floors already carved at its two ends (carve the
   * rooms first).
   */
  corridor(pts: V2[], width: number, o: CarveOptions & { ys?: number[] } = {}): this {
    if (pts.length < 2) throw new Error('corridor needs 2+ points');
    let ys: number[] | null = o.ys ?? null;
    if (!ys && o.y === undefined) {
      const a = pts[0] as V2;
      const b = pts[pts.length - 1] as V2;
      const ya = this.floorHeightIfCarved(a);
      const yb = this.floorHeightIfCarved(b);
      const y0 = ya ?? yb ?? 0;
      const y1 = yb ?? ya ?? 0;
      let total = 0;
      const cum = [0];
      for (let i = 1; i < pts.length; i++) {
        total += dist(pts[i - 1] as V2, pts[i] as V2);
        cum.push(total);
      }
      ys = cum.map((c) => lerp(y0, y1, total > 0 ? c / total : 0));
    }
    if (ys && ys.length !== pts.length) throw new Error('corridor ys must match pts');
    return this.addCarve({ kind: 'path', pts, hw: width / 2, ys }, o, o.y ?? 0);
  }

  /** Cuts a pit (floor drops by `depth`) or a rock mass (rises `depth`) out of the floor. */
  hole(shape: HoleShape, mode: 'pit' | 'rock', depth = 3): this {
    const sh: Shape = shape.kind === 'circle' ? { ...shape } : rectShape(shape.x, shape.z, shape.hw, shape.hd, shape.rot ?? 0, shape.round ?? 0.8);
    this.holes.push({ shape: sh, bound: boundOf(sh), mode, depth });
    return this;
  }

  /** Paints the critical-path runner (and keeps scatter off it). */
  runner(pts: V2[], width = 2.6): this {
    this.runners.push({ pts, width });
    for (const s of alongPolyline(pts, 3)) this.reserve(s.p, width * 0.6);
    return this;
  }

  /** Paints a disc (floor sigils, fissures...). */
  paintCircle(x: number, z: number, r: number, layer: number, soft = 1): this {
    this.paints.push({ layer, fn: (px, pz) => 1 - smoothstep(r - soft, r, Math.hypot(px - x, pz - z)) });
    return this;
  }

  /** Paints an annulus between r0 and r1. */
  paintRing(x: number, z: number, r0: number, r1: number, layer: number, soft = 0.5): this {
    this.paints.push({
      layer,
      fn: (px, pz) => {
        const d = Math.hypot(px - x, pz - z);
        return smoothstep(r0 - soft, r0, d) * (1 - smoothstep(r1, r1 + soft, d));
      },
    });
    return this;
  }

  /** Paints a band along a polyline. */
  paintLine(pts: V2[], width: number, layer: number, soft = 1): this {
    this.paints.push({ layer, fn: (px, pz) => band(polylineDistance({ x: px, z: pz }, pts), width, soft) });
    return this;
  }

  // -------------------------------------------------------------- queries

  /** Signed distance to the floor edge: negative on the floor. */
  sdf(x: number, z: number): number {
    let d = Infinity;
    for (const c of this.carves) {
      const b = c.bound;
      if (Math.hypot(x - b.x, z - b.z) - b.r >= d) continue;
      evalShape(c.shape, c.y, x, z);
      if (S.d < d) d = S.d;
    }
    for (const h of this.holes) {
      const b = h.bound;
      if (Math.hypot(x - b.x, z - b.z) - b.r > -d) continue;
      evalShape(h.shape, 0, x, z);
      if (-S.d > d) d = -S.d;
    }
    return d;
  }

  /** True when `p` is on the floor at least `margin` metres from the edge. */
  isFloor(p: V2, margin = 0): boolean {
    return this.sdf(p.x, p.z) < -margin;
  }

  /** Blended floor height at a point (ignores cliffs). */
  floorY(x: number, z: number): number {
    this.probe(x, z);
    return this.pr.y;
  }

  private floorHeightIfCarved(p: V2): number | null {
    if (this.carves.length === 0) return null;
    this.probe(p.x, p.z);
    return this.pr.d < 0.5 ? this.pr.y : null;
  }

  private probe(x: number, z: number): void {
    const n = this.carves.length;
    const pr = this.pr;
    let dmin = Infinity;
    let bi = -1;
    for (let i = 0; i < n; i++) {
      const c = this.carves[i] as Carve;
      const b = c.bound;
      if (Math.hypot(x - b.x, z - b.z) - b.r > dmin + BLEND * 6) {
        this.ds[i] = Infinity;
        continue;
      }
      evalShape(c.shape, c.y, x, z);
      this.ds[i] = S.d;
      this.dys[i] = S.y;
      this.dnz[i] = S.nz;
      if (S.d < dmin) {
        dmin = S.d;
        bi = i;
      }
    }
    let ws = 0;
    let ys = 0;
    let ts = 0;
    let fs = 0;
    const o = this.dp;
    for (let i = 0; i < n; i++) {
      const e = (this.ds[i] as number) - dmin;
      if (e > BLEND * 6) continue;
      const w = Math.exp(-e / BLEND);
      const edge = (this.carves[i] as Carve).edge;
      ws += w;
      ys += w * (this.dys[i] as number);
      if (edge === 'wall') {
        const near = smoothstep(0.1, 0.65, this.dnz[i] as number);
        ts += w * lerp(o.wallHeight, o.nearWallHeight, near);
        fs += w * (1 - near);
      } else ts += w * (edge === 'drop' ? -o.dropDepth : o.nearWallHeight);
    }
    pr.y = ws > 0 ? ys / ws : 0;
    pr.top = ws > 0 ? ts / ws : 0;
    pr.far = ws > 0 ? fs / ws : 0;
    pr.carve = bi >= 0 ? (this.carves[bi] as Carve) : null;
    pr.hole = null;
    let d = dmin;
    for (const h of this.holes) {
      const b = h.bound;
      if (Math.hypot(x - b.x, z - b.z) - b.r > -d) continue;
      evalShape(h.shape, 0, x, z);
      if (-S.d > d) {
        d = -S.d;
        pr.hole = h;
      }
    }
    pr.d = d;
  }

  private readonly carveHeight = (x: number, z: number): number => {
    this.probe(x, z);
    const pr = this.pr;
    const o = this.dp;
    let h = pr.y + (fbm2(x * 0.35, z * 0.35, this.o.seed + 11, 2) - 0.5) * 2 * o.floorNoise;
    if (pr.d <= 0) return h;
    const rough = (fbm2(x * 0.22 + 5.3, z * 0.22 - 3.1, this.o.seed + 17, 3) - 0.5) * 2 * o.wallRough;
    const hole = pr.hole;
    if (hole) {
      if (hole.mode === 'pit') return h - hole.depth * smoothstep(0, 1.6, pr.d);
      const k = smoothstep(FLAT * 0.5, FLAT + RAMP * 0.6, pr.d);
      return h + hole.depth * k + rough * k;
    }
    const k = smoothstep(FLAT, FLAT + RAMP, pr.d);
    const beyond = Math.min(8, o.wallSlope * Math.max(0, pr.d - FLAT - RAMP));
    h += pr.top * k + pr.far * beyond + rough * k * (0.3 + 0.7 * pr.far);
    return h;
  };

  private readonly carveSurface = (x: number, z: number, w: [number, number, number, number]): void => {
    this.probe(x, z);
    const pr = this.pr;
    const P = this.dp.paint;
    const acc = [0, 0, 0, 0];
    const add = (i: number, v: number): void => {
      acc[i] = (acc[i] ?? 0) + v;
    };
    const edge = smoothstep(-0.7, 0.8, pr.d);
    if (edge < 1) {
      const f = 1 - edge;
      let run = 0;
      for (const r of this.runners) run = Math.max(run, band(polylineDistance({ x, z }, r.pts), r.width, 1.4));
      const n = fbm2(x * 0.08 + 13.1, z * 0.08 - 7.3, this.o.seed + 3, 3);
      const a = smoothstep(0.56, 0.74, n) * this.dp.accentAmount + smoothstep(-2.4, -0.2, pr.d) * this.dp.accentAmount * 0.6;
      add(pr.carve?.layer ?? P.floor, f * 2);
      add(P.path, f * run * 4);
      add(P.accent, f * a * 3);
    }
    add(P.wall, edge * 3);
    for (const p of this.paints) {
      const k = p.fn(x, z);
      if (k > 0) add(p.layer, k * 4 * (1 - edge));
    }
    for (let i = 0; i < 4; i++) w[i] = acc[i] ?? 0;
  };

  // -------------------------------------------------------------- dressing helpers

  private pick(pieces: readonly KitPiece[]): KitPiece {
    return this.rng.weighted(pieces.map((p) => ({ weight: p.weight ?? 1, value: p })));
  }

  private scaleOf(p: KitPiece): number {
    return Array.isArray(p.scale) ? this.rng.range(p.scale[0], p.scale[1]) : (p.scale ?? 1);
  }

  /** Places a kit piece (random scale within its range). */
  piece(p: KitPiece, x: number, z: number, rot: number, extra: Omit<PropInstance, 'model' | 'x' | 'z'> = {}): this {
    const s = this.scaleOf(p);
    const inst: PropInstance = { model: p.model, x, z, rot, scale: s, ...extra };
    if (p.tint !== undefined) inst.tint = p.tint;
    this.props.push(inst);
    return this;
  }

  /** Point on a circle at a clock hour (12 = north/-z, 3 = east/+x). */
  static clock(x: number, z: number, r: number, hour: number): V2 {
    const a = (hour / 12) * Math.PI * 2;
    return { x: x + Math.sin(a) * r, z: z - Math.cos(a) * r };
  }

  /** `n` evenly spaced points on a circle, starting at `startHour`. */
  static ring(x: number, z: number, r: number, n: number, startHour = 0): V2[] {
    return Array.from({ length: n }, (_, i) => DungeonBuilder.clock(x, z, r, startHour + (i * 12) / n));
  }

  /**
   * Clock-face landmarks just outside a round arena's rim: `far` pieces on the
   * north half, `near` (low) pieces toward the camera. Lights alternate in
   * when given. Returns the landmark positions.
   */
  rim(x: number, z: number, r: number, count: number, far: KitPiece, near: KitPiece | null, opts: { light?: KitPiece; lightEvery?: number; offset?: number; startHour?: number } = {}): V2[] {
    const off = opts.offset ?? 1.1;
    const pts = DungeonBuilder.ring(x, z, r + off, count, opts.startHour ?? 0);
    pts.forEach((p, i) => {
      const nz = (p.z - z) / (r + off);
      const rot = Math.atan2(x - p.x, z - p.z);
      const lit = opts.light && opts.lightEvery && i % opts.lightEvery === 0;
      if (lit && opts.light) this.piece(opts.light, p.x, p.z, rot);
      else if (nz > 0.42) {
        if (near) this.piece(near, p.x, p.z, rot);
      } else this.piece(far, p.x, p.z, rot);
    });
    return pts;
  }

  /** Arch spanning a corridor mouth; `facing` is the direction of travel. */
  arch(x: number, z: number, facing: number, width: number, tint?: number): this {
    const inst: PropInstance = { model: 'arch', x, z, rot: facing, scale: Math.max(0.7, (width + 0.6) / ARCH_WIDTH) };
    if (tint !== undefined) inst.tint = tint;
    this.props.push(inst);
    return this;
  }

  /** Portal with a frame and two flanking lights; `facing` is the direction a player walks into it. */
  litPortal(def: PortalDef, facing: number, light: KitPiece = { model: 'brazier' }, frameTint?: number): this {
    this.portal(def);
    const fx = Math.sin(facing);
    const fz = Math.cos(facing);
    const frame: PropInstance = { model: 'portal_frame', x: def.x + fx * 0.8, z: def.z + fz * 0.8, rot: facing };
    if (frameTint !== undefined) frame.tint = frameTint;
    this.props.push(frame);
    const tx = fz;
    const tz = -fx;
    const o = def.radius + 1.4;
    this.piece(light, def.x + tx * o, def.z + tz * o, facing);
    this.piece(light, def.x - tx * o, def.z - tz * o, facing);
    return this;
  }

  /** Random floor points `margin` metres from the edge, clear of reserved spots. */
  floorSpots(count: number, minDist: number, opts: { margin?: number; area?: { minX: number; minZ: number; maxX: number; maxZ: number }; accept?: (p: V2) => boolean } = {}): V2[] {
    const area = opts.area ?? { minX: this.minX, minZ: this.minZ, maxX: this.maxX, maxZ: this.maxZ };
    const margin = opts.margin ?? 1.5;
    return scatter(this.rng, { count, ...area, minDist, maxTries: count * 80, accept: (p) => this.sdf(p.x, p.z) < -margin && !this.isReserved(p, 0.5) && (opts.accept ? opts.accept(p) : true) });
  }

  /** Scatters pieces over the floor (with optional colliders). */
  scatterFloor(pieces: (KitPiece & { collider?: Collider; noShadow?: boolean })[], count: number, minDist: number, opts: Parameters<DungeonBuilder['floorSpots']>[2] = {}): this {
    for (const p of this.floorSpots(count, minDist, opts)) {
      const k = this.rng.weighted(pieces.map((x) => ({ weight: x.weight ?? 1, value: x })));
      const extra: Omit<PropInstance, 'model' | 'x' | 'z'> = {};
      if (k.collider) extra.collider = k.collider;
      if (k.noShadow) extra.noShadow = true;
      this.piece(k, p.x, p.z, this.rng.range(0, Math.PI * 2), extra);
      this.reserve(p, 1);
    }
    return this;
  }

  /** Random points beyond the floor edge, `band[0]..band[1]` metres out (cliff and rim scenery). */
  beyondSpots(count: number, minDist: number, band: [number, number]): V2[] {
    return scatter(this.rng, {
      count,
      minX: this.minX + 2,
      minZ: this.minZ + 2,
      maxX: this.maxX - 2,
      maxZ: this.maxZ - 2,
      minDist,
      maxTries: count * 60,
      accept: (p) => {
        const d = this.sdf(p.x, p.z);
        return d > band[0] && d < band[1];
      },
    });
  }

  /**
   * Scatters collider-free scenery beyond the floor edge (trees, boulders,
   * spires). Camera-facing slopes get `near` pieces instead (keep them low),
   * or nothing, so the quarter-view camera always sees the floor.
   */
  scatterBeyond(pieces: KitPiece[], count: number, minDist: number, band: [number, number], near: KitPiece[] = []): this {
    for (const p of this.beyondSpots(count, minDist, band)) {
      const camSide = this.normalAt(p.x, p.z).z > 0.42;
      if (camSide && near.length === 0) continue;
      this.piece(this.pick(camSide ? near : pieces), p.x, p.z, this.rng.range(0, Math.PI * 2));
    }
    return this;
  }

  // -------------------------------------------------------------- build

  override build(): ZoneLayout {
    if (!this.finished) {
      this.finished = true;
      this.finish();
    }
    return super.build();
  }

  private finish(): void {
    const step = CONTOUR_STEP;
    const nx = Math.ceil(this.o.width / step);
    const nz = Math.ceil(this.o.depth / step);
    const W = nx + 1;
    const vals = new Float64Array(W * (nz + 1));
    for (let j = 0; j <= nz; j++) {
      for (let i = 0; i <= nx; i++) {
        let v = this.carves.length > 0 ? this.sdf(this.minX + i * step, this.minZ + j * step) : 1;
        if (i === 0 || j === 0 || i === nx || j === nz) v = Math.max(v, 1);
        vals[j * W + i] = v;
      }
    }
    const inset = this.dp.inset;
    const only = this.carves.length === 1 ? this.carves[0] : undefined;
    if (only && only.shape.kind === 'circle' && this.holes.length === 0) {
      // buildNav erodes circles by the agent radius itself.
      this.walkable = { kind: 'circle', x: only.shape.x, z: only.shape.z, r: only.shape.r - inset + AGENT_RADIUS };
    } else {
      const loops = marchingSquares(vals, nx, nz, this.minX, this.minZ, step, -inset).map((l) => simplifyRing(l, 0.06));
      this.walkable = { kind: 'poly', pts: bridgeLoops(loops, { x: this.minX, z: this.minZ }) };
    }
    this.edgeLoops = marchingSquares(vals, nx, nz, this.minX, this.minZ, step, 0).map((l) => simplifyRing(l, 0.03));
    this.lineEdges();
  }

  private normalAt(x: number, z: number): V2 {
    const e = 0.2;
    const gx = this.sdf(x + e, z) - this.sdf(x - e, z);
    const gz = this.sdf(x, z + e) - this.sdf(x, z - e);
    const l = Math.hypot(gx, gz) || 1;
    return { x: gx / l, z: gz / l };
  }

  private lineEdges(): void {
    const kit = this.dp.kit;
    const wallScale = kit.wall?.scale ?? 1;
    const spacing = kit.wall ? kit.wall.length * wallScale : 6;
    for (const loop of this.edgeLoops) {
      const ring = [...loop, loop[0] as V2];
      let slot = 0;
      for (const s of alongPolyline(ring, spacing)) {
        const { x, z } = s.p;
        const n = this.normalAt(x, z);
        this.probe(x + n.x * 0.3, z + n.z * 0.3);
        const owner = this.pr.carve;
        if (this.pr.hole || !owner || !owner.walls) continue;
        slot++;
        const near = n.z > 0.42 || owner.edge !== 'wall';
        const rot = Math.atan2(-n.x, -n.z);
        if (owner.path && kit.light && slot % kit.light.every === 0) {
          this.piece(kit.light, x + n.x * 0.45, z + n.z * 0.45, rot);
          continue;
        }
        if (near) {
          if (kit.low && kit.low.length > 0 && this.rng.chance(0.55)) {
            this.piece(this.pick(kit.low), x + n.x * 0.75, z + n.z * 0.75, rot + this.rng.range(-0.4, 0.4));
          }
          continue;
        }
        if (kit.post && slot % kit.post.every === 0) {
          this.piece(kit.post, x + n.x * 0.75, z + n.z * 0.75, rot);
          continue;
        }
        if (kit.wall) {
          const off = 0.1 + ((kit.wall.depth ?? 1.2) * wallScale) / 2;
          const cx = x + n.x * off;
          const cz = z + n.z * off;
          const hl = spacing / 2;
          const clear = this.sdf(cx + n.z * hl, cz - n.x * hl) > 0.15 && this.sdf(cx - n.z * hl, cz + n.x * hl) > 0.15;
          if (clear) this.piece(kit.wall, cx, cz, rot);
          else if (kit.post) this.piece(kit.post, x + n.x * 0.75, z + n.z * 0.75, rot);
        }
      }
    }
    if (kit.rocks && kit.rocks.length > 0) {
      const rs = kit.rockSpacing ?? 3;
      for (const loop of this.edgeLoops) {
        for (const s of alongPolyline([...loop, loop[0] as V2], rs)) {
          const n = this.normalAt(s.p.x, s.p.z);
          this.probe(s.p.x + n.x * 0.3, s.p.z + n.z * 0.3);
          const edge = this.pr.hole ? 'wall' : (this.pr.carve?.edge ?? 'wall');
          if (edge === 'drop') continue;
          const near = n.z > 0.42 || edge === 'low';
          if (near && this.rng.chance(0.5)) continue;
          const p = this.pick(near && kit.nearRocks ? kit.nearRocks : kit.rocks);
          const sc = this.scaleOf(p) * (near && !kit.nearRocks ? 0.45 : 1);
          const foot = (p.r ?? 1) * sc;
          const off = foot * 0.7 + (near ? this.rng.range(0.5, 1.2) : this.rng.range(0.9, 2.6));
          const x = s.p.x + n.x * off + this.rng.range(-0.6, 0.6);
          const z = s.p.z + n.z * off + this.rng.range(-0.6, 0.6);
          if (this.sdf(x, z) < foot * 0.6 + 0.6) continue;
          const inst: PropInstance = { model: p.model, x, z, rot: this.rng.range(0, Math.PI * 2), scale: sc, y: -0.35 * sc };
          if (p.tint !== undefined) inst.tint = p.tint;
          this.props.push(inst);
        }
      }
    }
    if (kit.clutter && kit.clutter.length > 0) {
      const chance = kit.clutterChance ?? 0.25;
      for (const loop of this.edgeLoops) {
        for (const s of alongPolyline([...loop, loop[0] as V2], 2.4)) {
          if (!this.rng.chance(chance)) continue;
          const n = this.normalAt(s.p.x, s.p.z);
          const off = this.rng.range(0.7, 1.4);
          const p = { x: s.p.x - n.x * off, z: s.p.z - n.z * off };
          if (this.sdf(p.x, p.z) > -0.5 || this.isReserved(p, 0.5)) continue;
          this.piece(this.pick(kit.clutter), p.x, p.z, this.rng.range(0, Math.PI * 2), { noShadow: true });
        }
      }
    }
  }
}
